import { formatDate, formatPrice, getStatusLabel } from './orderUtils';

/**
 * Génère le contenu HTML pour l'impression des commandes
 */
export const generatePrintContent = (orders) => {
  const currentDate = new Date().toLocaleDateString('fr-FR');
  
  return `
    <!DOCTYPE html>
    <html>
      <head>
        <title>Commandes - ${currentDate}</title>
        <style>
          body {
            font-family: Arial, sans-serif;
            margin: 20px;
            color: #333;
            background: white;
          }
          .header {
            text-align: center;
            border-bottom: 2px solid #333;
            padding-bottom: 20px;
            margin-bottom: 30px;
          }
          .order {
            page-break-inside: avoid;
            margin-bottom: 40px;
            border: 1px solid #ddd;
            padding: 20px;
            border-radius: 8px;
          }
          .order-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 20px;
            padding-bottom: 10px;
            border-bottom: 1px solid #eee;
          }
          .order-info {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 20px;
            margin-bottom: 20px;
          }
          .info-section h4 {
            margin: 0 0 10px 0;
            color: #333;
            font-size: 14px;
            text-transform: uppercase;
          }
          .info-section p {
            margin: 5px 0;
            font-size: 12px;
          }
          .items-table {
            width: 100%;
            border-collapse: collapse;
            margin-top: 20px;
          }
          .items-table th,
          .items-table td {
            border: 1px solid #ddd;
            padding: 8px;
            text-align: left;
            font-size: 12px;
          }
          .items-table th {
            background-color: #f5f5f5;
            font-weight: bold;
          }
          .status {
            padding: 4px 8px;
            border-radius: 4px;
            font-size: 11px;
            font-weight: bold;
            text-transform: uppercase;
          }
          .status.confirmed { background-color: #d4edda; color: #155724; }
          .status.pending { background-color: #fff3cd; color: #856404; }
          .status.cancelled { background-color: #f8d7da; color: #721c24; }
          .status.shipped { background-color: #cce5ff; color: #004085; }
          .status.delivered { background-color: #d1ecf1; color: #0c5460; }
          .totals {
            margin-top: 20px;
            text-align: right;
          }
          .totals table {
            margin-left: auto;
            border-collapse: collapse;
          }
          .totals td {
            padding: 5px 10px;
            border: none;
          }
          .totals .total-row {
            font-weight: bold;
            border-top: 2px solid #333;
          }
          @media print {
            body { margin: 0; }
            .order { page-break-inside: avoid; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>COMMANDES INKLAND</h1>
          <p>Imprimé le ${currentDate}</p>
        </div>
        
        ${orders.map(order => `
          <div class="order">
            <div class="order-header">
              <h2>Commande ${order.orderNumber}</h2>
              <span class="status ${order.status}">${getStatusLabel(order.status)}</span>
            </div>
            
            <div class="order-info">
              <div class="info-section">
                <h4>Informations de livraison</h4>
                <p><strong>Nom:</strong> ${order.deliveryFirstName} ${order.deliveryLastName}</p>
                <p><strong>Adresse:</strong> ${order.deliveryAddress}</p>
                <p><strong>Ville:</strong> ${order.deliveryPostalCode} ${order.deliveryCity}</p>
                <p><strong>Pays:</strong> ${order.deliveryCountry}</p>
              </div>
              
              <div class="info-section">
                <h4>Informations de facturation</h4>
                <p><strong>Nom:</strong> ${order.billingFirstName} ${order.billingLastName}</p>
                <p><strong>Adresse:</strong> ${order.billingAddress}</p>
                <p><strong>Ville:</strong> ${order.billingPostalCode} ${order.billingCity}</p>
                <p><strong>Pays:</strong> ${order.billingCountry}</p>
              </div>
            </div>
            
            <table class="items-table">
              <thead>
                <tr>
                  <th>Produit</th>
                  <th>Variante</th>
                  <th>Quantité</th>
                  <th>Prix unitaire</th>
                  <th>Total</th>
                </tr>
              </thead>
              <tbody>
                ${order.items.map(item => `
                  <tr>
                    <td>${item.productName}</td>
                    <td>${item.variantName || '-'}</td>
                    <td>${item.quantity}</td>
                    <td>${formatPrice(item.unitPrice)}</td>
                    <td>${formatPrice(item.totalPrice)}</td>
                  </tr>
                `).join('')}
              </tbody>
            </table>
            
            <div class="totals">
              <table>
                <tr>
                  <td>Sous-total HT:</td>
                  <td>${formatPrice(order.subtotal)}</td>
                </tr>
                <tr>
                  <td>Frais de port:</td>
                  <td>${formatPrice(order.shippingCost)}</td>
                </tr>
                <tr>
                  <td>TVA:</td>
                  <td>${formatPrice(order.taxAmount)}</td>
                </tr>
                <tr class="total-row">
                  <td><strong>TOTAL TTC:</strong></td>
                  <td><strong>${formatPrice(order.totalAmount)}</strong></td>
                </tr>
              </table>
            </div>
            
            ${order.notes ? `<p><strong>Notes:</strong> ${order.notes}</p>` : ''}
          </div>
        `).join('')}
      </body>
    </html>
  `;
};

/**
 * Génère le contenu HTML pour une facture
 */
export const generateInvoiceContent = (order) => {
  const currentDate = new Date().toLocaleDateString('fr-FR');
  const payment = order.payments?.find(p => p.status === 'succeeded');
  
  return `
    <!DOCTYPE html>
    <html>
      <head>
        <title>Facture ${order.orderNumber}</title>
        <style>
          body {
            font-family: Arial, sans-serif;
            margin: 0;
            padding: 20px;
            color: #333;
            background: white;
          }
          .invoice-header {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            margin-bottom: 40px;
            border-bottom: 3px solid #333;
            padding-bottom: 20px;
          }
          .company-info {
            flex: 1;
          }
          .invoice-info {
            text-align: right;
            flex: 1;
          }
          .invoice-title {
            font-size: 32px;
            font-weight: bold;
            color: #333;
            margin: 0 0 10px 0;
          }
          .invoice-number {
            font-size: 18px;
            color: #666;
            margin: 0;
          }
          .billing-info {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 40px;
            margin-bottom: 40px;
          }
          .info-section h3 {
            margin: 0 0 15px 0;
            color: #333;
            font-size: 16px;
            text-transform: uppercase;
            border-bottom: 1px solid #ddd;
            padding-bottom: 5px;
          }
          .info-section p {
            margin: 5px 0;
            font-size: 14px;
          }
          .items-table {
            width: 100%;
            border-collapse: collapse;
            margin-bottom: 30px;
          }
          .items-table th,
          .items-table td {
            border: 1px solid #ddd;
            padding: 12px;
            text-align: left;
          }
          .items-table th {
            background-color: #f8f9fa;
            font-weight: bold;
            color: #333;
          }
          .items-table .text-right {
            text-align: right;
          }
          .totals {
            margin-left: auto;
            width: 300px;
          }
          .totals table {
            width: 100%;
            border-collapse: collapse;
          }
          .totals td {
            padding: 8px 12px;
            border: none;
          }
          .totals .total-row {
            font-weight: bold;
            border-top: 2px solid #333;
            font-size: 16px;
          }
          .payment-info {
            margin-top: 30px;
            padding: 20px;
            background-color: #f8f9fa;
            border-radius: 8px;
          }
          .payment-info h3 {
            margin: 0 0 15px 0;
            color: #333;
          }
          .footer {
            margin-top: 50px;
            text-align: center;
            color: #666;
            font-size: 12px;
            border-top: 1px solid #ddd;
            padding-top: 20px;
          }
          @media print {
            body { margin: 0; padding: 15px; }
          }
        </style>
      </head>
      <body>
        <div class="invoice-header">
          <div class="company-info">
            <h1 class="invoice-title">INKLAND</h1>
            <p>Votre boutique de tatouage</p>
            <p>123 Rue du Tatouage<br>
            75001 Paris, France<br>
            contact@inkland.fr</p>
          </div>
          <div class="invoice-info">
            <h1 class="invoice-title">FACTURE</h1>
            <p class="invoice-number">N° ${order.orderNumber}</p>
            <p><strong>Date d'émission:</strong> ${currentDate}</p>
            <p><strong>Date de commande:</strong> ${formatDate(order.createdAt)}</p>
          </div>
        </div>
        
        <div class="billing-info">
          <div class="info-section">
            <h3>Facturer à</h3>
            <p><strong>${order.billingFirstName} ${order.billingLastName}</strong></p>
            <p>${order.billingAddress}</p>
            <p>${order.billingPostalCode} ${order.billingCity}</p>
            <p>${order.billingCountry}</p>
          </div>
          
          <div class="info-section">
            <h3>Livrer à</h3>
            <p><strong>${order.deliveryFirstName} ${order.deliveryLastName}</strong></p>
            <p>${order.deliveryAddress}</p>
            <p>${order.deliveryPostalCode} ${order.deliveryCity}</p>
            <p>${order.deliveryCountry}</p>
          </div>
        </div>
        
        <table class="items-table">
          <thead>
            <tr>
              <th>Description</th>
              <th>Variante</th>
              <th class="text-right">Quantité</th>
              <th class="text-right">Prix unitaire HT</th>
              <th class="text-right">Total HT</th>
            </tr>
          </thead>
          <tbody>
            ${order.items.map(item => `
              <tr>
                <td>${item.productName}</td>
                <td>${item.variantName || '-'}</td>
                <td class="text-right">${item.quantity}</td>
                <td class="text-right">${formatPrice(item.unitPrice)}</td>
                <td class="text-right">${formatPrice(item.totalPrice)}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
        
        <div class="totals">
          <table>
            <tr>
              <td>Sous-total HT:</td>
              <td class="text-right">${formatPrice(order.subtotal)}</td>
            </tr>
            <tr>
              <td>Frais de port:</td>
              <td class="text-right">${formatPrice(order.shippingCost)}</td>
            </tr>
            <tr>
              <td>TVA (20%):</td>
              <td class="text-right">${formatPrice(order.taxAmount)}</td>
            </tr>
            <tr class="total-row">
              <td>TOTAL TTC:</td>
              <td class="text-right">${formatPrice(order.totalAmount)}</td>
            </tr>
          </table>
        </div>
        
        ${payment ? `
          <div class="payment-info">
            <h3>Informations de paiement</h3>
            <p><strong>Méthode de paiement:</strong> ${payment.paymentMethod === 'card' ? 'Carte bancaire' : payment.paymentMethod}</p>
            <p><strong>Montant payé:</strong> ${formatPrice(payment.amount)}</p>
            <p><strong>Date de paiement:</strong> ${formatDate(payment.processedAt)}</p>
            <p><strong>Statut:</strong> <span style="color: green; font-weight: bold;">PAYÉ</span></p>
          </div>
        ` : ''}
        
        <div class="footer">
          <p>Merci pour votre commande !</p>
          <p>Cette facture a été générée automatiquement le ${currentDate}</p>
        </div>
      </body>
    </html>
  `;
};
